import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { GuidService } from 'ui-components/services/guid-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';

import { useIsSellerInTrial } from '../../../../../hooks/use-is-seller-in-trial';
import { useBTEmailExperienceNew } from '../../../../../hooks/use-seller-setting';
import { BackendActionTypeIds } from '../../generated/action-type-ids';
import type { WorkflowStep, WorkflowActionDraft, WorkflowEntityType } from '../../types';
import { getActionRegistry } from '../../registries';
import { useWorkflowsContext } from '../../context';
import { messages } from './messages';
import styles from './styles.module.less';

type ActionSectionProps = {
  step: WorkflowStep;
  entityType: WorkflowEntityType;
  onUpdateStep: (step: WorkflowStep) => void;
};

export const ActionSection: React.FC<ActionSectionProps> = ({ step, entityType, onUpdateStep }) => {
  const intl = useIntl();
  const isInTrial = useIsSellerInTrial();
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const actions = useMemo(() => step.actions ?? [], [step.actions]);
  const actionRegistry = useMemo(() => getActionRegistry(entityType), [entityType]);
  const { accessibleActionTypeIds } = useWorkflowsContext();

  const hasFulfillmentProviderFilter = useMemo(
    () => step.filters?.some((f) => f.fieldName === 'SellerFillProviderId') ?? false,
    [step.filters],
  );

  const actionTypeOptions = useMemo(
    () =>
      Object.values(actionRegistry)
        .reduce<Array<{ value: string; label: string }>>((acc, entry) => {
          if (accessibleActionTypeIds !== null && !accessibleActionTypeIds.has(entry.actionTypeId)) {
            return acc;
          }
          if ((isInTrial || enableNewEmailExperience) && entry.actionTypeId === BackendActionTypeIds.Email) {
            return acc;
          }
          if (!hasFulfillmentProviderFilter && entry.actionTypeId === BackendActionTypeIds.DelegateFulfillmentProviderService) {
            return acc;
          }
          acc.push({
            value: String(entry.actionTypeId),
            label: accessibleActionTypeIds?.get(entry.actionTypeId) ?? (entry.labelMessage ? intl.formatMessage(entry.labelMessage) : ''),
          });
          return acc;
        }, [])
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        }),
    [actionRegistry, accessibleActionTypeIds, intl, isInTrial, enableNewEmailExperience, hasFulfillmentProviderFilter],
  );

  const handleAddAction = useCallback(() => {
    const newAction: WorkflowActionDraft = {
      id: GuidService.newGuid(),
      actionTypeId: 0,
      parameter: null,
      intParameter: null,
    };
    onUpdateStep({ ...step, actions: [...actions, newAction] });
  }, [step, actions, onUpdateStep]);

  const handleUpdateAction = useCallback(
    (actionId: string, updates: Partial<WorkflowActionDraft>) => {
      const updatedActions = actions.map((action) => (action.id === actionId ? { ...action, ...updates } : action));
      onUpdateStep({ ...step, actions: updatedActions });
    },
    [step, actions, onUpdateStep],
  );

  const handleRemoveAction = useCallback(
    (actionId: string) => {
      const updatedActions = actions.filter((action) => action.id !== actionId);
      onUpdateStep({ ...step, actions: updatedActions });
    },
    [step, actions, onUpdateStep],
  );

  return (
    <FlexColumn gap={Gap.Space12}>
      {actions.map((action) => {
        const registryEntry = actionRegistry[action.actionTypeId];
        const ValueInput = registryEntry?.valueInput ?? null;
        const valueInputLabel = registryEntry?.labelMessage ? intl.formatMessage(registryEntry.labelMessage) : '';
        const auxiliaryInput = registryEntry?.auxiliaryInput ?? null;
        const AuxiliaryInput = auxiliaryInput?.isVisible(action) ? auxiliaryInput.component : null;

        return (
          <FlexRow key={action.id} className={styles['action-row']} gap={Gap.Space16}>
            <div className={styles['action-properties']}>
              <SearchableDropdownField
                className={styles['action-select']}
                id={`${action.id}-type`}
                label={intl.formatMessage(messages.actionType)}
                onItemSelected={(value) =>
                  handleUpdateAction(action.id, { actionTypeId: Number(value), parameter: null, intParameter: null })
                }
                options={actionTypeOptions}
                placeholder={intl.formatMessage(messages.selectActionType)}
                selectedItemValue={action.actionTypeId > 0 ? String(action.actionTypeId) : ''}
              />
              {ValueInput && (
                <div>
                  <Text textType={TextType.Label} value={valueInputLabel} />
                  <ValueInput
                    className={styles['value-input']}
                    draft={action}
                    onUpdate={(updates) => handleUpdateAction(action.id, updates)}
                  />
                </div>
              )}
              {AuxiliaryInput && <AuxiliaryInput draft={action} onUpdate={(updates) => handleUpdateAction(action.id, updates)} />}
            </div>
            <div className={styles['remove-button-container']}>
              <Button
                ariaLabel={intl.formatMessage(messages.removeActionAriaLabel)}
                className={styles['remove-button']}
                onClick={() => handleRemoveAction(action.id)}
                theme={THEME_CONSTANTS.unstyled}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          </FlexRow>
        );
      })}
      <Button onClick={handleAddAction} theme={THEME_CONSTANTS.tertiary}>
        {intl.formatMessage(messages.addActionButton)}
      </Button>
    </FlexColumn>
  );
};
